<template>
  <div v-if="loadingPlans" class="flex justify-center items-center py-12 w-full">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    <span class="ml-4 text-muted-foreground">Loading subscriptions...</span>
  </div>
  <form v-else @submit.prevent="handleSubmit">
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
      <label
        v-for="plan in recurringPlans"
        :key="plan.id"
        class="subscription-plan-card"
        :class="{
          'subscription-plan-card--selected': isSelected(plan.id)
        }"
        :aria-checked="isSelected(plan.id)"
        role="radio"
        tabindex="0"
      >
        <input
          type="radio"
          name="plan_id"
          :value="plan.id"
          v-model="selectedPlanId"
          class="sr-only"
        />
        <div v-if="plan.product.images?.length" class="subscription-plan-card__image">
          <img :src="plan.product.images[0]" :alt="plan.product.name + ' image'" />
        </div>
        <div class="subscription-plan-card__title">{{ plan.product.name }}</div>
        <div class="subscription-plan-card__description">{{ plan.product.description }}</div>
        <ul v-if="plan.product.marketing_features?.length" class="subscription-plan-card__features">
          <li v-for="feature in plan.product.marketing_features" :key="getFeatureKey(feature)" class="subscription-plan-card__feature">
            <theme-icon icon="check-circle" type="icons" class="subscription-plan-card__feature-icon" />
            <span>{{ getFeatureName(feature) }}</span>
          </li>
        </ul>
        <div class="subscription-plan-card__price">
          {{ formatCurrencyCents(plan.unit_amount, plan.currency) }}
          <span class="subscription-plan-card__interval" v-if="plan.recurring">/ {{ plan.recurring.interval }}</span>
          <span v-else>One-time</span>
        </div>
      </label>
    </div>
    <div class="subscription-actions">
      <themed-button 
        type="button" 
        variant="secondary" 
        @click="$emit('prev')"
      >
        Back
      </themed-button>
      <themed-button 
        type="submit" 
        variant="default" 
        :disabled="!selectedPlanId"
      >
        Continue
      </themed-button>
    </div>
  </form>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';
import ThemedButton from '../ThemedButton.vue';
import axios from 'axios';
import { formatCurrencyCents } from '../../../../utils/formatters';

export default {
  name: "SubscriptionStep",
  components: { 
    ThemeIcon,
    ThemedButton
  },
  props: {
    selectedPlan: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      selectedPlanId: this.selectedPlan,
      loadingPlans: false,
      plans: []
    }
  },
  watch: {
    selectedPlan(newVal) {
      this.selectedPlanId = newVal
    }
  },
  computed: {
    recurringPlans() {
      return this.plans.filter(
        plan =>
          plan.recurring &&
          plan.product &&
          plan.product.metadata?.category === "plan"
      );
    }
  },
  methods: {
    formatCurrencyCents,
    async fetchPlans() {
      this.loadingPlans = true;
      try {
        const res = await axios.get('/signup/stripe-plans');
        this.plans = res.data.data || [];
        this.$emit('plans-loaded', this.plans);
      } catch (error) {
        this.plans = [];
        this.$notify({ type: 'error', title: 'Error', text: 'Could not fetch subscription plans.' });
      } finally {
        this.loadingPlans = false;
      }
    },
    handleSubmit() {
      if (!this.selectedPlanId) {
        this.$notify({
          type: "error",
          title: "Error",
          text: "Please select a plan."
        });
        return;
      }
      this.$emit("plan-selected", this.selectedPlanId);
    },
    isSelected(planId) {
      return this.selectedPlanId === planId;
    },
    getFeatureName(feature) {
      return typeof feature === "string"
        ? feature
        : feature.name || feature.id || "";
    },
    getFeatureKey(feature) {
      return typeof feature === "string"
        ? feature
        : feature.id || feature.name || JSON.stringify(feature);
    }
  },
  created() {
    this.fetchPlans();
  }
};
</script>
